/**
 * ProvincialKPIs — real government data from data.go.th.
 * Replaces the stub "MUNICIPALITY OVERVIEW" that showed mocked numbers.
 *
 * Data sources (สำนักงานจังหวัดนครราชสีมา on data.go.th):
 *   Population  — ประชากรจากการทะเบียน รายตำบล
 *   Tourism     — สำนักงานการท่องเที่ยวและกีฬาจังหวัดนครราชสีมา
 *   Accidents   — คดีอุบัติเหตุจราจร + สถิติอุบัติเหตุรายอำเภอ
 *   Welfare     — ผู้สูงอายุ + ผู้มีบัตรประจำตัวคนพิการ
 */

import { fmtN } from "../lib/provincial";

interface Kpi {
  label: string;
  value: string;
  sub?: string;
  color?: string;
}

export interface ProvincialKPIs {
  population: { total: number; male: number; female: number; year: number } | null;
  tourism: {
    year: number | null;
    totalVisitors: number | null;
    thaiVisitors: number | null;
    foreignVisitors: number | null;
    revenueMillionBaht: number | null;
  } | null;
  accidents: { incidents: number; year: number } | null;
  hotspotDistrict: { name: string; count: number; year: number } | null;
  welfare: { elderly: number; disabled: number } | null;
}

import { PanelHeader } from "./PanelHeader";
import type { FallbackTier } from "@sikhio/shared";

interface Props {
  data: ProvincialKPIs | null;
  loading: boolean;
  ageMinutes?: number | null;
  fallbackTier?: FallbackTier;
}

function KpiTile({ label, value, sub, color }: Kpi) {
  return (
    <div
      style={{ display: "flex", flexDirection: "column", gap: 1 }}
      role="status"
      aria-label={`${label}: ${value}${sub ? `, ${sub}` : ""}`}
    >
      <div className="eyebrow">{label}</div>
      <div className="mono" style={{ fontSize: "var(--size-h2)", color: color ?? "var(--text)", lineHeight: 1.05 }}>
        {value}
      </div>
      {sub && <div className="eyebrow mono" style={{ color: "var(--text-3)" }}>{sub}</div>}
    </div>
  );
}

export function ProvincialKPIs({ data, loading, ageMinutes, fallbackTier }: Props) {
  if (loading && !data) {
    return (
      <div className="col" role="status" aria-busy="true" aria-label="Loading provincial KPIs">
        <PanelHeader title="นครราชสีมา " ageMinutes={ageMinutes} fallbackTier={fallbackTier} source="data.go.th" />
        <div className="skeleton" style={{ height: 28, marginTop: 8 }} />
        <div className="skeleton" style={{ height: 28, marginTop: 6 }} />
      </div>
    );
  }
  if (!data) return null;

  const { population, tourism, accidents, hotspotDistrict, welfare } = data;

  const kpis: Kpi[] = [];

  if (population) {
    kpis.push({
      label: "POPULATION",
      value: fmtN(population.total),
      sub: `♂ ${fmtN(population.male)} · ♀ ${fmtN(population.female)} · ${population.year}`,
    });
  }

  if (tourism?.totalVisitors) {
    kpis.push({
      label: "VISITORS / YEAR",
      value: fmtN(tourism.totalVisitors),
      sub: tourism.year ? `${tourism.year} · ฿${fmtN(tourism.revenueMillionBaht)}M revenue` : undefined,
      color: "var(--data)",
    });
  }

  if (tourism?.foreignVisitors != null) {
    kpis.push({
      label: "FOREIGN VISITORS",
      value: fmtN(tourism.foreignVisitors),
      sub: tourism.year ? `${tourism.year}` : undefined,
    });
  }

  if (accidents) {
    kpis.push({
      label: "TRAFFIC ACCIDENT CASES",
      value: fmtN(accidents.incidents),
      sub: `${accidents.year}`,
      color: "var(--bad)",
    });
  }

  if (hotspotDistrict && hotspotDistrict.count > 0) {
    kpis.push({
      label: "ACCIDENT HOTSPOT",
      value: hotspotDistrict.name.replace("อำเภอ", "").trim() || hotspotDistrict.name,
      sub: `${hotspotDistrict.count} accidents · ${hotspotDistrict.year}`,
      color: "var(--warn)",
    });
  }

  if (welfare) {
    kpis.push({
      label: "VULNERABLE PERSONS",
      value: fmtN(welfare.elderly + welfare.disabled),
      sub: `${fmtN(welfare.elderly)} elderly · ${fmtN(welfare.disabled)} disabled`,
    });
  }

  return (
    <div className="col" style={{ gap: 8 }}>
      <PanelHeader title="นครราชสีมา " ageMinutes={ageMinutes} fallbackTier={fallbackTier} source="data.go.th" />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px" }}>
        {kpis.map((k) => <KpiTile key={k.label} {...k} />)}
      </div>
      <div className="eyebrow mono" style={{ color: "var(--text-3)" }}>
        SOURCE · DATA.GO.TH · สำนักงานจังหวัดนครราชสีมา + กระทรวงท่องเที่ยวและกีฬา
      </div>
    </div>
  );
}
